import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import service from '../service';
import type {
  ToastGuidGroup,
  UpdateToastGuidGroupPayload,
} from '../types/toastGuidGroup';
import { toastGuidGroupQueryKeys } from './useToastGuidGroups';

export interface UpdateToastGuidGroupVariables {
  id: string;
  payload: UpdateToastGuidGroupPayload;
}

export function useUpdateToastGuidGroup(
  options?: UseMutationOptions<
    ToastGuidGroup,
    Error,
    UpdateToastGuidGroupVariables
  >,
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options ?? {};

  return useMutation({
    mutationKey: ['toastGuidGroup', 'update'],
    mutationFn: async ({ id, payload }: UpdateToastGuidGroupVariables) => {
      return service.updateToastGuidGroup(id, payload);
    },
    retry: (failureCount, error: any) => {
      const status =
        error?.status ??
        error?.response?.status ??
        error?.cause?.status ??
        null;
      const isServerOrNetwork = status == null || status >= 500;
      return failureCount < 2 && isServerOrNetwork;
    },
    ...restOptions,
    onSuccess: (data, variables, context, mutation) => {
      queryClient.invalidateQueries({ queryKey: toastGuidGroupQueryKeys.all });
      queryClient.invalidateQueries({
        queryKey: toastGuidGroupQueryKeys.detail(variables.id),
      });
      onSuccess?.(data, variables, context, mutation);
    },
  });
}
